<template>
  <div class="space-y-6">
    <div class="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
      <div>
        <h1 class="text-3xl font-black tracking-tight text-slate-900">Expéditions</h1>
        <p class="mt-2 text-slate-600 font-medium">Liste, statuts, étiquettes et suivi (à brancher sur l’API Laravel).</p>
      </div>
      <button class="h-11 px-5 rounded-xl bg-primary text-white font-black shadow-lg shadow-primary/20 opacity-60" disabled>
        Nouvelle expédition
      </button>
    </div>

    <div class="rounded-2xl bg-white border border-primary/10 p-5">
      <div class="grid grid-cols-1 lg:grid-cols-[1fr_220px_220px] gap-3">
        <div class="relative">
          <span class="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">search</span>
          <input
            v-model="query"
            class="h-12 w-full rounded-xl border border-primary/15 bg-white pl-12 pr-4 text-sm font-semibold outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
            placeholder="Rechercher : référence, destinataire, tracking…"
            type="text"
          />
        </div>
        <select
          v-model="status"
          class="h-12 w-full rounded-xl border border-primary/15 bg-white px-4 text-sm font-semibold outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
        >
          <option value="">Tous les statuts</option>
          <option value="draft">Brouillon</option>
          <option value="label_created">Étiquette créée</option>
          <option value="in_transit">En transit</option>
          <option value="delivered">Livré</option>
        </select>
        <select
          v-model="carrier"
          class="h-12 w-full rounded-xl border border-primary/15 bg-white px-4 text-sm font-semibold outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
        >
          <option value="">Tous les transporteurs</option>
          <option value="dhl">DHL</option>
          <option value="dpd">DPD</option>
          <option value="colissimo">Colissimo</option>
        </select>
      </div>
    </div>

    <div class="rounded-2xl bg-white border border-primary/10 overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full text-left">
          <thead class="bg-background-light/60 border-b border-primary/10">
            <tr class="text-xs font-black tracking-widest uppercase text-slate-500">
              <th class="px-6 py-4">Référence</th>
              <th class="px-6 py-4">Destinataire</th>
              <th class="px-6 py-4">Transporteur</th>
              <th class="px-6 py-4">Statut</th>
              <th class="px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="5" class="px-6 py-10 text-center">
                <div class="inline-block size-8 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
                <p class="mt-2 text-slate-500 font-bold">Chargement des expéditions...</p>
              </td>
            </tr>
            <template v-else>
              <tr v-for="row in filtered" :key="row.id" class="border-b border-primary/5 hover:bg-slate-50 transition-colors">
                <td class="px-6 py-4">
                  <div class="font-black text-slate-900">{{ row.tracking_number || 'En attente' }}</div>
                  <div class="text-[10px] text-slate-400 font-black uppercase tracking-widest">Ordre #{{ row.external_order_id }}</div>
                </td>
                <td class="px-6 py-4">
                  <div class="font-bold text-slate-800">{{ row.customer_name }}</div>
                  <div class="text-sm text-slate-500 font-medium">{{ row.city }}, {{ row.destination_country }}</div>
                </td>
                <td class="px-6 py-4">
                  <div class="font-bold text-slate-800">{{ getCarrierName(row) }}</div>
                </td>
                <td class="px-6 py-4">
                  <span class="status-chip" :class="statusClass(row.status)">{{ statusLabel(row.status) }}</span>
                </td>
                <td class="px-6 py-4 text-right">
                  <div class="flex justify-end gap-2">
                    <button 
                      v-if="row.label_url"
                      @click="downloadLabel(row.label_url)"
                      class="h-9 px-4 rounded-lg bg-emerald-50 text-emerald-600 font-bold text-xs hover:bg-emerald-600 hover:text-white transition-all flex items-center gap-2"
                      title="Télécharger l'étiquette PDF"
                    >
                      <span class="material-symbols-outlined text-sm">download</span>
                      Étiquette
                    </button>
                    <button 
                      @click="viewDetails(row)"
                      class="h-9 px-4 rounded-lg bg-primary/10 text-primary font-bold text-xs hover:bg-primary hover:text-white transition-all flex items-center gap-2"
                      title="Suivre le colis"
                    >
                      <span class="material-symbols-outlined text-sm">local_shipping</span>
                      Suivre
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="filtered.length === 0">
                <td class="px-6 py-10 text-center text-slate-500 font-semibold" colspan="5">Aucune expédition trouvée</td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'ShipmentsPage',
  data() {
    return {
      loading: true,
      shipments: [],
      query: '',
      status: '',
      carrier: ''
    }
  },
  computed: {
    filtered() {
      let list = [...this.shipments]
      
      if (this.query) {
        const q = this.query.toLowerCase()
        list = list.filter(s => 
          (s.tracking_number || '').toLowerCase().includes(q) ||
          (s.customer_name || '').toLowerCase().includes(q) ||
          (s.external_order_id || '').toString().includes(q)
        )
      }
      
      if (this.status) {
        list = list.filter(s => s.status === this.status)
      }
      
      if (this.carrier) {
        list = list.filter(s => (s.shipping_method_id || '').toLowerCase().includes(this.carrier.toLowerCase()))
      }
      
      return list
    }
  },
  async mounted() {
    await this.fetchShipments()
  },
  methods: {
    async fetchShipments() {
      this.loading = true
      try {
        const response = await axios.get('/ugo/shipments')
        const payload = response.data
        
        if (payload.success) {
          // Correction : Utiliser la clé `shipments` comme indiqué dans la documentation API
          const rawData = payload.shipments || []
          this.shipments = Array.isArray(rawData) ? rawData : []
          console.log('✅ Expéditions chargées via `payload.shipments`:', this.shipments.length)
        }
      } catch (e) {
        console.error('Erreur chargement expéditions:', e)
      } finally {
        this.loading = false
      }
    },
    statusLabel(statut) {
      const labels = {
        'pending': 'En attente',
        'ready_to_send': 'Prêt à l\'envoi',
        'in_transit': 'En transit',
        'delivered': 'Livré',
        'shipped': 'Expédié',
        'cancelled': 'Annulé',
        'label_created': 'Étiquette créée'
      }
      return labels[statut] || statut
    },
    statusClass(statut) {
      const classes = {
        'pending': 'bg-amber-100 text-amber-700',
        'ready_to_send': 'bg-amber-100 text-amber-700',
        'label_created': 'bg-indigo-100 text-indigo-700',
        'shipped': 'bg-blue-100 text-blue-700',
        'in_transit': 'bg-indigo-100 text-indigo-700',
        'delivered': 'bg-green-100 text-green-700',
        'cancelled': 'bg-red-100 text-red-700'
      }
      return classes[statut] || 'bg-slate-100 text-slate-700'
    },
    downloadLabel(url) {
      if (url) window.open(url, '_blank')
    },
    viewDetails(row) {
      this.$router.push(`/app/commandes/${row.external_order_id}`)
    },
    getCarrierName(row) {
      if (!row) return 'Inconnu'
      
      // 1. Essayer de trouver le nom dans le payload stocké
      if (row.full_payload?.selected_method_name) return row.full_payload.selected_method_name
      if (row.full_payload?.carrier) return row.full_payload.carrier
      
      // 2. Si on n'a que l'ID, essayer de le rendre lisible
      const id = String(row.shipping_method_id || '').toLowerCase()
      if (!id || id === 'null') return 'Non défini'
      
      // Mappage manuel étendu pour les cas courants (carrier_uuid UGO et Slugs)
      const common = {
        '8': 'Colissimo',
        'colissimo': 'Colissimo',
        'dhl': 'DHL Express',
        'chronopost': 'Chronopost',
        'mondial_relay': 'Mondial Relay',
        'ups': 'UPS',
        'colis_prive': 'Colis Privé',
        'relais_colis': 'Relais Colis',
        'gls': 'GLS'
      }

      if (common[id]) return common[id]

      // 3. Fallback : Formater le slug (ex: "colissimo-point-relais" -> "Colissimo Point Relais")
      const cleanId = id.includes(':') ? id.split(':')[1] : id
      return cleanId.split(/[-_]/).map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
    }
  }
}
</script>

<style scoped>
.action-btn {
  height: 36px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid rgba(26, 34, 127, 0.18);
  font-weight: 800;
  color: rgb(26 34 127);
  background: rgba(246, 246, 248, 0.6);
  opacity: 0.6;
}
.status-chip {
  display: inline-flex;
  align-items: center;
  height: 28px;
  padding: 0 10px;
  border-radius: 9999px;
  font-weight: 900;
  font-size: 12px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.status-chip.is-draft {
  background: rgba(148, 163, 184, 0.18);
  color: rgb(71 85 105);
  border: 1px solid rgba(148, 163, 184, 0.35);
}
.status-chip.is-label {
  background: rgba(26, 34, 127, 0.1);
  color: rgb(26 34 127);
  border: 1px solid rgba(26, 34, 127, 0.22);
}
.status-chip.is-transit {
  background: rgba(14, 116, 144, 0.12);
  color: rgb(14 116 144);
  border: 1px solid rgba(14, 116, 144, 0.22);
}
.status-chip.is-delivered {
  background: rgba(22, 163, 74, 0.12);
  color: rgb(22 163 74);
  border: 1px solid rgba(22, 163, 74, 0.22);
}
</style>
